import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { exportPreviewApi } from '@/features/DataHub/api/exportPreview.api';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { useSelectedSymbols } from '@/features/DataHub/hooks/useSelectedSymbols';
import {
  exportPreviewKeys,
  getSymbolsQueryKey
} from '@/features/DataHub/queries/keys/exportPreview.keys';
import buildUrl from '@/utils/buildUrl';

import useCodePreview from './useCodePreview';
import useDownloadFileContent from './useDownloadFileContent';
import { useExportSettingsContext } from './useExportSettingsContext';

const useJsonCsvPreview = (isCSV: boolean, capturePosthogEvent: () => void, enabled = true) => {
  const { exportType, includeMetadata, from, to, period, periodNumber } =
    useExportSettingsContext();
  const { timezoneValue } = useDataHubUserConfig();
  const userTimezone = timezoneValue;

  const symbols = useSelectedSymbols();

  const { createRequestToValue, createRequestFromValue } = useCodePreview(
    exportType,
    from,
    to,
    period,
    periodNumber
  );

  const { downloadFileContent } = useDownloadFileContent(capturePosthogEvent);
  const headers = useMemo(() => (isCSV ? { Accept: 'text/csv' } : undefined), [isCSV]);
  const symbolsKey = useMemo(() => getSymbolsQueryKey(symbols), [symbols]);

  const fromValue = createRequestFromValue();
  const toValue = createRequestToValue();

  const combineCsvData = useCallback((responses: string[]): string => {
    if (!responses.length) return '';

    const [firstResponse, ...otherResponses] = responses;
    const [header, ...rows] = firstResponse.split('\n').filter(Boolean);
    const combinedRows = [...rows];

    for (const response of otherResponses) {
      const [, ...dataRows] = response.split('\n');
      combinedRows.push(...dataRows.filter(Boolean));
    }

    return [header, ...combinedRows].join('\n');
  }, []);

  const {
    data: fileContent,
    isLoading: loading,
    isError: error
  } = useQuery({
    queryKey: exportPreviewKeys.list({
      symbolsKey,
      from: fromValue,
      to: toValue,
      includeMetadata,
      exportType,
      isCSV,
      userTimezone
    }),
    enabled: enabled && symbolsKey.length > 0,
    queryFn: async () => {
      const requests = symbols
        .filter(({ Code }) => !!Code)
        .map(({ Code, Period, PeriodType, TimeRef }) => {
          const indexURL = buildUrl('/index', {
            code: Code,
            period: Period,
            periodType: PeriodType,
            timeRef: TimeRef,
            from: fromValue,
            timezone: userTimezone,
            to: toValue,
            metadata: includeMetadata
          });

          return exportPreviewApi.fetchIndexData(indexURL, headers);
        });

      const results = await Promise.all(requests);

      if (isCSV) {
        const csvData = combineCsvData(results);
        return csvData;
      }
      const flattenedData = results.flat();
      return JSON.stringify(flattenedData, null, 2);
    },
    // CSV content is static for given inputs; avoid aggressive refetching.
    staleTime: 1000 * 60 // 1 minute
  });

  const downloadFile = useCallback(() => {
    if (fileContent) {
      downloadFileContent(fileContent, isCSV ? 'csv' : 'json');
    }
  }, [fileContent, downloadFileContent, isCSV]);

  return { downloadFile, loading, error, fileContent };
};

export default useJsonCsvPreview;
